import { Helmet } from "react-helmet-async";
import ParallaxElements from "@/components/ParallaxElements";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Portfolio from "@/components/Portfolio";
import BottomLineSection from "@/components/BottomLineSection";
import SectionDivider from "@/components/SectionDivider";

import { useIsMobile } from "@/hooks/use-mobile";
import AgencyTrap from "@/components/AgencyTrap";
import ComparisonTable from "@/components/ComparisonTable";
import HowItWorks from "@/components/HowItWorks";
import Pricing from "@/components/Pricing";

import FounderSection from "@/components/FounderSection";
import FAQ from "@/components/FAQ";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import CursorSparkles from "@/components/CursorSparkles";
import FloatingContact from "@/components/FloatingContact";

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://waveandwirewebdesigns.com/#localbusiness",
  "name": "Wave & Wire Web Design",
  "alternateName": "Wave & Wire",
  "description": "Agency-grade websites without the agency price tag. Custom web design for Hamptons businesses. You own the code. No hidden fees.",
  "url": "https://waveandwirewebdesigns.com",
  "logo": "https://waveandwirewebdesigns.com/wave-wire-logo.png",
  "image": "https://waveandwirewebdesigns.com/wave-wire-logo.png",
  "telephone": "+1-631-707-0672",
  "email": "aj@waveandwirewebdesigns.com",
  "priceRange": "$1,000 - $5,000+",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Hamptons",
    "addressRegion": "NY",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "40.9634",
    "longitude": "-72.1848"
  },
  "areaServed": [
    {
      "@type": "Place",
      "name": "The Hamptons, NY"
    },
    {
      "@type": "Place",
      "name": "North Fork, NY"
    },
    {
      "@type": "Place",
      "name": "NYC Metro Area"
    }
  ],
  "serviceType": ["Web Design", "Web Development", "Brand Identity", "SEO"],
  "sameAs": [],
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "18:00"
  }
};

const Index = () => {
  const isMobile = useIsMobile();

  return (
    <>
      <Helmet>
        <title>Hamptons Web Design | Wave & Wire | Agency Quality, Freelance Rates</title>
        <meta name="description" content="Stop paying agency retainers. Wave & Wire builds premium, custom websites for Hamptons businesses. You own the code. No hidden fees. Book your strategy call." />
        <link rel="canonical" href="https://waveandwirewebdesigns.com/" />
        <meta property="og:title" content="Hamptons Web Design | Wave & Wire" />
        <meta property="og:description" content="Agency-grade websites without the agency price tag. Custom web design for Hamptons, North Fork, and NYC Metro businesses." />
        <meta property="og:url" content="https://waveandwirewebdesigns.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Wave & Wire Web Design" />
        <meta property="og:image" content="https://waveandwirewebdesigns.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://waveandwirewebdesigns.com/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>
      <CursorSparkles />
      <main className="min-h-screen bg-background text-foreground overflow-x-hidden">
        <ParallaxElements />
        <Navbar />
        <Hero />
        <SectionDivider />
        <AgencyTrap />
        <SectionDivider />
        <Portfolio />
        <SectionDivider />
        <BottomLineSection />
        <SectionDivider />
        <ComparisonTable />
        <SectionDivider />
        <HowItWorks />
        <SectionDivider />
        <Pricing isMobile={isMobile} />
        <SectionDivider />
        <FounderSection />
        <SectionDivider />
        {!isMobile && <><FAQ /><SectionDivider /></>}
        <Contact />
        <Footer />
        <FloatingContact />
      </main>
    </>
  );
};

export default Index;
